import { apiRequest, ApiError } from "./api";
import { session } from "./session";

export interface LoginResult {
  success: boolean;
  requires2FA?: boolean;
  message?: string;
  verifyTwoFactorOtpToken?: string | null;
  pendingData?: Record<string, unknown>;
}

function extractLoginData(response: Record<string, unknown>): Record<string, unknown> {
  const data = response.data;
  if (data && typeof data === "object") {
    return data as Record<string, unknown>;
  }
  return response;
}

export function applySessionFromLoginData(data: Record<string, unknown>): void {
  const user = data.user as Record<string, unknown> | undefined;
  const accessToken = data.accessToken as string | undefined;
  if (!accessToken) {
    throw new ApiError("Login response did not include an access token");
  }

  const userId = (user?.id ?? user?.userId) as number | undefined;
  if (userId == null) {
    throw new ApiError("Login response did not include a user id");
  }

  session.setSession({
    accessToken,
    userId,
    userName: (user?.name as string | undefined) ?? undefined,
    userEmail: (user?.email as string | undefined) ?? undefined,
  });
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await apiRequest<Record<string, unknown>>("/auth/login", {
    method: "POST",
    body: { email, password },
    auth: false,
  });

  if (response.status !== true) {
    throw new ApiError(
      typeof response.message === "string" ? response.message : "Login failed"
    );
  }

  const data = extractLoginData(response);
  if (data.isTwoFactorEnabled === true) {
    const verifyToken =
      (data.verifyTwoFactorOtpToken as string | undefined) ??
      (data.accessToken as string | undefined) ??
      null;

    return {
      success: false,
      requires2FA: true,
      message: typeof response.message === "string" ? response.message : undefined,
      verifyTwoFactorOtpToken: verifyToken,
      pendingData: data,
    };
  }

  applySessionFromLoginData(data);
  return {
    success: true,
    message: typeof response.message === "string" ? response.message : undefined,
  };
}

export async function verifyOtp(
  identifier: string,
  otp: string,
  verifyToken: string,
  pendingData: Record<string, unknown>
): Promise<void> {
  const response = await apiRequest<Record<string, unknown>>("/otp/verify", {
    method: "POST",
    body: { identifier, purpose: "2FA", otp },
    auth: false,
    bearerToken: verifyToken,
  });

  const verifyData = extractLoginData(response);
  const merged: Record<string, unknown> = { ...pendingData, ...verifyData };
  if (!merged.accessToken && verifyData.accessToken) {
    merged.accessToken = verifyData.accessToken;
  }

  applySessionFromLoginData(merged);
}

export async function resendOtp(identifier: string): Promise<void> {
  await apiRequest("/otp/send", {
    method: "POST",
    body: { identifier, purpose: "2FA" },
    auth: false,
  });
}

export function logout(): void {
  session.clearSession();
}
